import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ALL_PRODUCTS, FEATURED_PRODUCTS, PRODUCTS_BY_ID,
         PRODUCTS_BY_COLLECTION, STATIC_COLLECTIONS } from '../lib/staticCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Single shared state — fetched ONCE, used by every page ──────────────────
const ProductsContext = createContext(null);

// Session memory — survives soft navigation, cleared only on hard reload
const SESSION = { products: null, collections: null, fetched: false };

export function ProductsProvider({ children }) {
  const [products, setProducts]         = useState(SESSION.products || ALL_PRODUCTS);
  const [collections, setCollections]   = useState(SESSION.collections || STATIC_COLLECTIONS);
  const [hydrated, setHydrated]         = useState(SESSION.fetched);
  const fetchedRef = useRef(SESSION.fetched);

  useEffect(() => {
    // Already fetched this session — skip
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    // Fire both requests in parallel, never block render
    Promise.all([
      axios.get(`${API}/products`).catch(() => null),
      axios.get(`${API}/collections`).catch(() => null),
    ]).then(([prodRes, colRes]) => {
      if (prodRes?.data?.length > 0) {
        SESSION.products = prodRes.data;
        setProducts(prodRes.data);
      }
      if (colRes?.data?.length > 0) {
        SESSION.collections = colRes.data;
        setCollections(colRes.data);
      }
      SESSION.fetched = true;
      setHydrated(true);
    });
  }, []);

  // Helper: get single product — from live data or static fallback
  function getProduct(id) {
    return products.find(p => p.id === id) || PRODUCTS_BY_ID[id] || null;
  }

  // Helper: get products for a collection
  function getByCollection(slug) {
    return products.filter(p =>
      p.collection === slug && p.is_visible !== false
    );
  }

  // Helper: get featured products
  const featured = products.filter(p => p.is_featured && p.is_visible !== false);

  return (
    <ProductsContext.Provider value={{
      products, collections, featured,
      hydrated, getProduct, getByCollection,
    }}>
      {children}
    </ProductsContext.Provider>
  );
}

export function useProducts() {
  const ctx = useContext(ProductsContext);
  if (!ctx) throw new Error('useProducts must be inside ProductsProvider');
  return ctx;
}